import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner, Badge } from 'react-bootstrap';
import { FaCopy, FaFolder, FaFolderOpen, FaBook, FaChevronRight, FaChevronDown } from 'react-icons/fa';
import { toast } from 'react-toastify';
import CourseService from '../../../services/courseService';
import AdminNotesService from '../../../services/adminNotesService';

const ImportNotesModal = ({ show, onHide, targetCourseId, onImportComplete }) => {
    const [loading, setLoading] = useState(false);
    const [loadingCourses, setLoadingCourses] = useState(true);
    const [importing, setImporting] = useState(false);
    const [courses, setCourses] = useState([]);
    const [sourceCourseId, setSourceCourseId] = useState('');
    const [sourceHierarchy, setSourceHierarchy] = useState([]);
    const [selectedTopics, setSelectedTopics] = useState({});
    const [targetFolders, setTargetFolders] = useState([]);
    const [targetParentId, setTargetParentId] = useState('');
    const [expandedItems, setExpandedItems] = useState({});

    // Reset state when modal opens
    useEffect(() => {
        if (show) {
            // Reset all selections
            setSourceCourseId('');
            setSelectedTopics({});
            setTargetParentId('');
            setExpandedItems({});
            loadCourses();
            loadTargetFolders();
        }
    }, [show, targetCourseId]);

    const loadCourses = async () => {
        try {
            setLoadingCourses(true);
            const response = await CourseService.getAllCourses();
            let coursesData = response.data || response || [];
            // Filter out current course
            coursesData = coursesData.filter(c => c.id !== targetCourseId);
            setCourses(coursesData);
        } catch (error) {
            console.error("Failed to load courses:", error);
            toast.error("Failed to load courses");
        } finally {
            setLoadingCourses(false);
        }
    };

    const loadTargetFolders = async () => {
        try {
            const response = await AdminNotesService.getHierarchy(targetCourseId);
            const folders = extractFolders(response.data || []);
            setTargetFolders([{ id: '', title: 'Root (Top Level)' }, ...folders]);
        } catch (error) {
            console.error("Failed to load target folders:", error);
        }
    };

    const extractFolders = (items, level = 0) => {
        let folders = [];
        items.forEach(item => {
            if (item.content_type === 'folder') {
                folders.push({ 
                    id: item.id, 
                    title: '—'.repeat(level) + ' ' + item.title,
                    level: level 
                });
                if (item.children) {
                    folders.push(...extractFolders(item.children, level + 1));
                }
            }
        });
        return folders;
    };

    const loadSourceHierarchy = async () => {
        if (!sourceCourseId) return;
        
        try {
            setLoading(true);
            const response = await AdminNotesService.getHierarchy(sourceCourseId);
            setSourceHierarchy(response.data || []);
            setSelectedTopics({});
        } catch (error) {
            console.error("Failed to load source hierarchy:", error);
            toast.error("Failed to load source course content");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (sourceCourseId) {
            loadSourceHierarchy();
        }
    }, [sourceCourseId]);

    const toggleExpand = (id) => {
        setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const toggleSelectTopic = (id) => {
        setSelectedTopics(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const selectAllInFolder = (item) => {
        const newSelected = { ...selectedTopics };
        
        const selectRecursive = (node) => {
            newSelected[node.id] = true;
            if (node.children) {
                node.children.forEach(child => selectRecursive(child));
            }
        };
        
        selectRecursive(item);
        setSelectedTopics(newSelected);
    };

    const handleImport = async () => {
        const selectedIds = Object.keys(selectedTopics).filter(key => selectedTopics[key]);
        
        if (selectedIds.length === 0) {
            toast.warning("Please select at least one topic or folder to import");
            return;
        }
        
        if (!sourceCourseId) {
            toast.warning("Please select a source course");
            return;
        }
        
        setImporting(true);
        
        try {
            const importData = {
                source_course_id: parseInt(sourceCourseId),
                target_course_id: parseInt(targetCourseId),
                topic_ids: selectedIds.map(id => parseInt(id)),
                target_parent_id: targetParentId || null
            };
            
            const response = await AdminNotesService.importFromCourse(importData);
            
            console.log("Import response:", response);
            
            if (response && response.status === true) {
                const importedCount = response.data?.imported || selectedIds.length;
                toast.success(response.message || `Successfully imported ${importedCount} items`);
                
                // Close the modal
                onHide();
                
                // Refresh the parent component after a short delay
                setTimeout(() => {
                    if (onImportComplete) {
                        onImportComplete();
                    }
                }, 500);
            } else {
                toast.error(response?.message || "Import failed");
            }
            
        } catch (error) {
            console.error("Import error:", error);
            toast.error(error.response?.data?.message || "Failed to import notes");
        } finally {
            setImporting(false);
        }
    };

    const countSelectedItems = () => {
        return Object.keys(selectedTopics).filter(key => selectedTopics[key]).length;
    };

    const renderSourceTree = (items, level = 0) => {
        return items.map(item => {
            const hasChildren = item.children && item.children.length > 0;
            const isExpanded = expandedItems[item.id];
            const isSelected = selectedTopics[item.id];
            const isFolder = item.content_type === 'folder';
            
            return (
                <div key={item.id} style={{ marginLeft: `${level * 24}px`, marginBottom: '5px' }}>
                    <div className="d-flex align-items-center py-1">
                        {hasChildren && (
                            <Button 
                                variant="link" 
                                size="sm" 
                                className="p-0 me-1"
                                onClick={() => toggleExpand(item.id)}
                                style={{ width: '20px' }}
                            >
                                {isExpanded ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
                            </Button>
                        )}
                        {!hasChildren && <div style={{ width: '20px' }} />}
                        
                        <Form.Check 
                            type="checkbox"
                            checked={isSelected || false}
                            onChange={() => toggleSelectTopic(item.id)}
                            className="me-2"
                        />
                        
                        <span className="me-2">
                            {isFolder ? 
                                (isExpanded ? <FaFolderOpen className="text-warning" /> : <FaFolder className="text-warning" />) : 
                                <FaBook className="text-primary" />
                            }
                        </span>
                        
                        <span>{item.title}</span>
                        
                        {isFolder && (
                            <Button 
                                variant="link" 
                                size="sm" 
                                className="ms-2 p-0 text-muted"
                                onClick={() => selectAllInFolder(item)}
                            >
                                (Select All)
                            </Button>
                        )}
                    </div>
                    
                    {hasChildren && isExpanded && (
                        <div>
                            {renderSourceTree(item.children, level + 1)}
                        </div>
                    )}
                </div>
            );
        });
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop="static" keyboard={!importing}>
            <Modal.Header closeButton={!importing}>
                <Modal.Title>
                    <FaCopy className="me-2 text-success" />
                    Import Notes from Another Course
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body>
                <Alert variant="info">
                    <strong>How it works:</strong> Select topics/folders from a source course and copy them to your current course.
                    All content (explanations, code blocks, exercises) will be duplicated.
                </Alert>
                
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>📖 Source Course *</Form.Label>
                            <Form.Select
                                value={sourceCourseId}
                                onChange={(e) => setSourceCourseId(e.target.value)}
                                disabled={loadingCourses || importing}
                            >
                                <option value="">-- Select a course --</option>
                                {courses.map(course => (
                                    <option key={course.id} value={course.id}>
                                        {course.name}
                                    </option>
                                ))}
                            </Form.Select>
                            {loadingCourses && <small>Loading courses...</small>}
                        </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>📁 Import to Folder (Optional)</Form.Label>
                            <Form.Select
                                value={targetParentId}
                                onChange={(e) => setTargetParentId(e.target.value)}
                                disabled={importing}
                            >
                                {targetFolders.map(folder => (
                                    <option key={folder.id} value={folder.id}>
                                        {folder.title}
                                    </option>
                                ))}
                            </Form.Select>
                            <small className="text-muted">Leave as "Root" for top level</small>
                        </Form.Group>
                    </Col>
                </Row>
                
                {sourceCourseId && (
                    <>
                        <hr />
                        <div className="mb-2">
                            <strong>Select content to import:</strong>
                            <Badge bg="primary" className="ms-2">
                                {countSelectedItems()} selected
                            </Badge>
                        </div>
                        
                        {loading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" size="sm" />
                                <p className="mt-2">Loading course content...</p>
                            </div>
                        ) : sourceHierarchy.length === 0 ? (
                            <Alert variant="warning">
                                No content found in this course.
                            </Alert>
                        ) : (
                            <div className="border rounded p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {renderSourceTree(sourceHierarchy)}
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={importing}>
                    Cancel
                </Button>
                <Button 
                    variant="success" 
                    onClick={handleImport} 
                    disabled={importing || !sourceCourseId || countSelectedItems() === 0}
                >
                    {importing ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Importing...
                        </>
                    ) : (
                        <>
                            <FaCopy className="me-2" />
                            Import Selected ({countSelectedItems()})
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ImportNotesModal;